import React, { useEffect, useState } from 'react';
import './BlogDetail.css';
import NotFound from './NotFound';
import { getApiUrl } from '../config';
import { submitForm } from '../utils/formSubmit';
import Swal from 'sweetalert2';

const formatBlogContent = (contentHtml) => {
  if (!contentHtml) return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(contentHtml, 'text/html');
    const container = doc.body;

    // Parse root-level nodes for FAQ questions (e.g. Q1: ...)
    const childNodes = Array.from(container.childNodes);
    const faqPairs = [];
    let currentQuestion = null;
    let currentAnswerNodes = [];

    childNodes.forEach((node) => {
      // Ignore nodes inside inline CTA boxes and Free Assessment form containers
      if (node.nodeType === 1 && (node.classList.contains('inline-cta-box') || node.classList.contains('free-assessment__form-container'))) {
        if (currentQuestion) {
          faqPairs.push({ q: currentQuestion, aNodes: currentAnswerNodes });
          currentQuestion = null;
          currentAnswerNodes = [];
        }
        return;
      }

      const isQuestionNode =
        (node.nodeType === 1 && // Element node (b, strong, span, h4, etc.)
          (node.tagName === 'B' || node.tagName === 'STRONG' || node.tagName === 'SPAN' || node.tagName === 'H4' || node.tagName === 'H3') &&
          /^(?:Q|Question)\s*\d+[\s\.\:-]/i.test(node.textContent.trim()))
        ||
        (node.nodeType === 3 && // Text node
          /^(?:Q|Question)\s*\d+[\s\.\:-]/i.test(node.textContent.trim()));

      const isHeaderNode =
        node.nodeType === 1 &&
        /^(?:H1|H2|H3|H4|H5|H6|HR|SECTION|DIV)$/i.test(node.tagName) &&
        !/^(?:Q|Question)\s*\d+[\s\.\:-]/i.test(node.textContent.trim()) &&
        !node.classList.contains('inline-cta-box') &&
        !node.classList.contains('free-assessment__form-container');

      if (isQuestionNode) {
        if (currentQuestion) {
          faqPairs.push({ q: currentQuestion, aNodes: currentAnswerNodes });
        }
        currentQuestion = node;
        currentAnswerNodes = [];
      } else if (isHeaderNode && currentQuestion) {
        // A header (like "Final Words") marks the end of the FAQ section
        faqPairs.push({ q: currentQuestion, aNodes: currentAnswerNodes });
        currentQuestion = null;
        currentAnswerNodes = [];
      } else {
        if (currentQuestion) {
          currentAnswerNodes.push(node);
        }
      }
    });

    if (currentQuestion) {
      faqPairs.push({ q: currentQuestion, aNodes: currentAnswerNodes });
    }

    if (faqPairs.length > 0) {
      const faqWrapper = doc.createElement('div');
      faqWrapper.className = 'blog-faq-section-wrapper';

      faqPairs.forEach((pair) => {
        const card = doc.createElement('div');
        card.className = 'blog-faq-card';

        const qEl = doc.createElement('p');
        qEl.className = 'blog-faq-question';
        if (pair.q.nodeType === 1) {
          qEl.innerHTML = pair.q.innerHTML;
        } else {
          qEl.textContent = pair.q.textContent;
        }

        const aEl = doc.createElement('p');
        aEl.className = 'blog-faq-answer';
        pair.aNodes.forEach((aNode) => {
          if (aNode.nodeType === 3) {
            const textContent = aNode.textContent.replace(/^\s*\n+/, '').trim();
            if (textContent !== '') {
              aEl.appendChild(doc.createTextNode(textContent + ' '));
            }
          } else {
            aEl.appendChild(aNode.cloneNode(true));
          }
        });

        card.appendChild(qEl);
        card.appendChild(aEl);
        faqWrapper.appendChild(card);
      });

      // Insert wrapper before the first question
      const firstQuestionNode = faqPairs[0].q;
      container.insertBefore(faqWrapper, firstQuestionNode);

      // Remove the original question and answer nodes
      faqPairs.forEach((pair) => {
        if (pair.q.parentNode) {
          pair.q.parentNode.removeChild(pair.q);
        }
        pair.aNodes.forEach((aNode) => {
          if (aNode.parentNode) {
            aNode.parentNode.removeChild(aNode);
          }
        });
      });
    }

    return container.innerHTML;
  } catch (err) {
    console.error('Error formatting HTML content:', err);
    return contentHtml;
  }
};


const BlogDetail = ({ slug }) => {
  const cachedPost = window.__BLOG_DETAIL_CACHE__ && window.__BLOG_DETAIL_CACHE__[slug];
  const [post, setPost] = useState(cachedPost || null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(!cachedPost);
  const [loadingSlug, setLoadingSlug] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const hasCache = window.__BLOG_DETAIL_CACHE__ && window.__BLOG_DETAIL_CACHE__[slug];
    setPost(hasCache || null);

    if (hasCache) {
      setLoading(false);
    } else {
      setLoading(true);

      // Fetch individual post details from backend
      fetch(getApiUrl(`/blogs/${slug}`))
        .then((res) => {
          if (!res.ok) throw new Error('Blog post not found');
          return res.json();
        })
        .then((data) => {
          setPost(data);
          if (!window.__BLOG_DETAIL_CACHE__) {
            window.__BLOG_DETAIL_CACHE__ = {};
          }
          window.__BLOG_DETAIL_CACHE__[slug] = data;
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching blog post:', err);
          setPost(null);
          setLoading(false);
        });
    }

    // Fetch all posts for recent list
    fetch(getApiUrl('/blogs'))
      .then((res) => res.json())
      .then((data) => {
        const filtered = (data || []).filter(p => p.slug !== slug).slice(0, 3);
        setRecentPosts(filtered);
      })
      .catch((err) => {
        console.error('Error fetching recent blogs:', err);
        setRecentPosts([]);
      });
  }, [slug]);

  // Update SEO metadata & Canonical tag when post details are loaded/changed
  useEffect(() => {
    if (post) {
      if (post.meta_title || post.title) {
        document.title = post.meta_title ? post.meta_title : `${post.title} | Al Quran Lesson`;
      }

      const metaDescText = post.meta_description || post.excerpt || `${post.title} - Learn more about Quranic topics at Al Quran Lesson.`;
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', metaDescText);

      // Set exact canonical URL matching the blog detail page URL (https://alquranlesson.com/<slug>/)
      const canonicalUrl = `https://alquranlesson.com/${slug}/`;
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonicalUrl);
    }
  }, [post, slug]);

  // Handle CTA and Assessment form submissions rendered inside dangerouslySetInnerHTML content
  useEffect(() => {
    const handleBlogFormsSubmit = async (e) => {
      // 1. Handle Inline CTA Box Form
      const ctaForm = e.target.closest('.inline-cta-box__form');
      if (ctaForm) {
        e.preventDefault();
        const emailInputEl = ctaForm.querySelector('input[type="email"]');
        const emailValue = emailInputEl ? emailInputEl.value.trim() : '';

        if (!emailValue) {
          Swal.fire({
            title: 'Please enter your email',
            icon: 'warning',
            confirmButtonColor: '#044b28'
          });
          return;
        }

        try {
          await submitForm('Blog Detail CTA Subscription', {
            email: emailValue,
            blogTitle: post ? post.title : '',
            blogSlug: slug
          });

          Swal.fire({
            title: 'Subscribed Successfully!',
            text: 'Thank you for enrolling in a free trial. Our team will contact you shortly.',
            icon: 'success',
            confirmButtonColor: '#044b28'
          });

          if (emailInputEl) {
            emailInputEl.value = '';
          }
        } catch (err) {
          console.error('Error submitting CTA form:', err);
          Swal.fire({
            title: 'Submission Failed',
            text: 'Could not send request. Please try again.',
            icon: 'error',
            confirmButtonColor: '#044b28'
          });
        }
        return;
      }

      // 2. Handle Embedded Free Assessment Form
      const assessmentForm = e.target.closest('.free-assessment__form');
      if (assessmentForm) {
        e.preventDefault();
        const firstName = assessmentForm.querySelector('input[name="firstName"]')?.value?.trim() || '';
        const lastName = assessmentForm.querySelector('input[name="lastName"]')?.value?.trim() || '';
        const course = assessmentForm.querySelector('select[name="course"]')?.value || 'Noorani Qaida';
        const country = assessmentForm.querySelector('select[name="country"]')?.value || '';
        const phone = assessmentForm.querySelector('input[name="phone"]')?.value?.trim() || '';
        const message = assessmentForm.querySelector('textarea[name="message"]')?.value?.trim() || '';

        if (!firstName || !lastName || !phone || !course || !country || country === 'Country' || !message) {
          Swal.fire({
            title: 'All Fields Are Required',
            text: 'Please fill in all fields (First Name, Last Name, Course, Country, Phone Number, and Message).',
            icon: 'warning',
            confirmButtonColor: '#044b28'
          });
          return;
        }

        try {
          await submitForm('Blog Free Assessment Form', {
            firstName,
            lastName,
            course,
            country: country !== 'Country' ? country : '',
            phone,
            message,
            blogTitle: post ? post.title : '',
            blogSlug: slug
          });

          Swal.fire({
            title: 'Assessment Request Sent!',
            text: 'Thank you for submitting! Our team will contact you shortly.',
            icon: 'success',
            confirmButtonColor: '#044b28'
          });

          assessmentForm.reset();
        } catch (err) {
          console.error('Error submitting assessment form:', err);
          Swal.fire({
            title: 'Submission Failed',
            text: 'Could not send request. Please try again.',
            icon: 'error',
            confirmButtonColor: '#044b28'
          });
        }
      }
    };

    document.addEventListener('submit', handleBlogFormsSubmit);
    return () => {
      document.removeEventListener('submit', handleBlogFormsSubmit);
    };
  }, [post, slug]);


  const handleBlogClick = (e, targetSlug) => {
    e.preventDefault();
    setLoadingSlug(targetSlug);

    fetch(getApiUrl(`/blogs/${targetSlug}`))
      .then((res) => {
        if (!res.ok) throw new Error('Failed to prefetch blog details');
        return res.json();
      })
      .then((data) => {
        if (!window.__BLOG_DETAIL_CACHE__) {
          window.__BLOG_DETAIL_CACHE__ = {};
        }
        window.__BLOG_DETAIL_CACHE__[targetSlug] = data;
        setLoadingSlug(null);
        window.history.pushState({}, '', `/${targetSlug}/`);
      })
      .catch((err) => {
        console.error('Error prefetching blog:', err);
        setLoadingSlug(null);
        // Fallback: navigate anyway so user is not stuck
        window.history.pushState({}, '', `/${targetSlug}/`);
      });
  };


  const handleSubscribe = (e) => {
    e.preventDefault();
    if (emailInput.trim()) {
      alert(`Thank you for subscribing with: ${emailInput}`);
      setEmailInput('');
    }
  };

  if (loading) {
    return (
      <div className="blog-detail-page" style={{ padding: '100px 0', textAlign: 'center' }}>
        <h2>Loading blog post...</h2>
      </div>
    );
  }

  if (!post) {
    return <NotFound />;
  }

  return (
    <div className="blog-detail-page">
      {/* Dynamic Hero Banner */}
      <section
        className="blog-detail-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.75)), url('${process.env.PUBLIC_URL}/assets/images/home-banner.jpg')`
        }}
      >
        <div className="blog-detail-hero__container">
          <h1 className="blog-detail-hero__title">{post.title}</h1>
          {/* Meta date, author, and time removed as requested */}
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="blog-detail-container">
        <main className="blog-detail-main">
          <article className="blog-detail-article">
            {/* Display Rich HTML Content returned from backend or static fallback */}
            <div
              className="blog-rich-content"
              dangerouslySetInnerHTML={{ __html: formatBlogContent(post.content) }}
            />
          </article>
        </main>

        {/* Sidebar */}
        <aside className="blog-sidebar">
          {/* Categories Widget */}
          {!isScrolled && (
            <div className="sidebar-widget categories-widget">
              <h3 className="widget-title">Categories</h3>
              <ul className="widget-categories">
                <li>
                  <a href="/blog/" className="active">
                    <span>Quran Guide</span>
                    <span className="count">12</span>
                  </a>
                </li>
                <li>
                  <a href="/blog/">
                    <span>Hifz Tips</span>
                    <span className="count">8</span>
                  </a>
                </li>
                <li>
                  <a href="/blog/">
                    <span>Tajweed Rules</span>
                    <span className="count">15</span>
                  </a>
                </li>
              </ul>
            </div>
          )}

          {/* Recent Posts Widget */}
          <div className="sidebar-widget">
            <h3 className="widget-title">Recent Posts</h3>
            <div className="widget-recent-posts">
              {recentPosts.map((p) => (
                <a key={p.id} href={`/${p.slug}/`} onClick={(e) => handleBlogClick(e, p.slug)} className="recent-post-item">
                  <div
                    className="recent-post-item__image"
                    style={{ backgroundImage: `url('${p.image && p.image.startsWith('http') ? p.image : (p.image && p.image.startsWith('/assets') ? process.env.PUBLIC_URL + p.image : process.env.PUBLIC_URL + '/assets/images/home-banner.jpg')}')` }}
                  >
                    {loadingSlug === p.slug && (
                      <div className="recent-post-spinner-overlay">
                        <span className="recent-post-spinner" />
                      </div>
                    )}
                  </div>
                  <div className="recent-post-item__content">
                    <h4 className="recent-post-item__title">
                      {loadingSlug === p.slug ? "Loading..." : p.title}
                    </h4>
                    <span className="recent-post-item__date">
                      {p.published_date ? new Date(p.published_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : p.date || 'July 03, 2026'}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Need Help/Ad Widget */}
          <div className="sidebar-widget promo-widget">
            <h4>Start Learning Today</h4>
            <p>Get matched with a certified tutor matching your schedule and preferences.</p>
            <a href="/free-trial/" className="sidebar-promo-btn">Book Free Lesson</a>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogDetail;
